import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import { Card } from '../components/ui';
import { NetworkGraph, GraphLegend, GraphFilters } from '../components/mappe';
import { LoadingSpinner } from '../components/ui/LoadingStates';

function MappePage() {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [graphData, setGraphData] = useState({ nodes: [], edges: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [rsaList, setRsaList] = useState([]);
  const [fornitoriList, setFornitoriList] = useState([]);
  const [tipologieList, setTipologieList] = useState([]);

  // Filtri da URL
  const selectedRsa = searchParams.get('rsaId') || null;
  const selectedFornitore = searchParams.get('fornitoreId') || null;
  const selectedTipologia = searchParams.get('tipologiaId') || null;

  // Carica liste RSA, Fornitori e Tipologie per i filtri
  useEffect(() => {
    const fetchLists = async () => {
      try {
        const [rsaResult, fornitoriResult, tipologieResult] = await Promise.all([
          apiRequest('/rsa'),
          apiRequest('/fornitori'),
          apiRequest('/mappe/tipologie')
        ]);

        setRsaList(
          (rsaResult.value || []).map((r) => ({
            id: r.id,
            nome: r.nome
          }))
        );

        setFornitoriList(
          (fornitoriResult.value || []).map((f) => ({
            id: f.id,
            nome: f.nome
          }))
        );

        setTipologieList(tipologieResult.value || []);
      } catch (error) {
        console.error('Error fetching filter lists:', error);
      }
    };

    fetchLists();
  }, [apiRequest]);

  // Carica dati grafo
  useEffect(() => {
    const fetchGraph = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedRsa) params.append('rsaId', selectedRsa);
        if (selectedFornitore) params.append('fornitoreId', selectedFornitore);
        if (selectedTipologia) params.append('tipologiaId', selectedTipologia);

        const queryString = params.toString();
        const url = `/mappe/network${queryString ? `?${queryString}` : ''}`;

        const data = await apiRequest(url);
        setGraphData(data);
      } catch (error) {
        console.error('Error fetching network graph:', error);
        showToast('Errore nel caricamento della mappa', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchGraph();
  }, [apiRequest, selectedRsa, selectedFornitore, selectedTipologia, showToast]);

  // Gestione filtri
  const handleRsaChange = useCallback(
    (rsaId) => {
      const newParams = new URLSearchParams(searchParams);
      if (rsaId) {
        newParams.set('rsaId', rsaId);
      } else {
        newParams.delete('rsaId');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleFornitoreChange = useCallback(
    (fornitoreId) => {
      const newParams = new URLSearchParams(searchParams);
      if (fornitoreId) {
        newParams.set('fornitoreId', fornitoreId);
      } else {
        newParams.delete('fornitoreId');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleTipologiaChange = useCallback(
    (tipologiaId) => {
      const newParams = new URLSearchParams(searchParams);
      if (tipologiaId) {
        newParams.set('tipologiaId', tipologiaId);
      } else {
        newParams.delete('tipologiaId');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleReset = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  // Navigazione doppio click
  const handleNodeDoubleClick = useCallback(
    (node) => {
      if (node.type === 'rsa' && node.data.entityId) {
        navigate(`/rsa/${node.data.entityId}`);
      } else if (node.type === 'fornitore' && node.data.entityId) {
        navigate(`/fornitori/${node.data.entityId}`);
      }
    },
    [navigate]
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mappa Rete RSA-Fornitori</h1>
          <p className="text-sm text-gray-500 mt-1">
            Cerca una tipologia di intervento per visualizzare i fornitori che la offrono e le RSA collegate
          </p>
        </div>
      </div>

      {/* Filtri */}
      <GraphFilters
        rsaList={rsaList}
        fornitoriList={fornitoriList}
        tipologieList={tipologieList}
        selectedRsa={selectedRsa}
        selectedFornitore={selectedFornitore}
        selectedTipologia={selectedTipologia}
        onRsaChange={handleRsaChange}
        onFornitoreChange={handleFornitoreChange}
        onTipologiaChange={handleTipologiaChange}
        onReset={handleReset}
      />

      {/* Grafo */}
      <Card className="p-0 overflow-hidden">
        <div className="h-[600px]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <LoadingSpinner />
            </div>
          ) : graphData.nodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <span className="text-4xl mb-2">🗺️</span>
              <p>Nessun dato da visualizzare</p>
              {(selectedRsa || selectedFornitore || selectedTipologia) && (
                <button
                  onClick={handleReset}
                  className="mt-2 text-sm text-primary-600 hover:underline"
                >
                  Rimuovi filtri
                </button>
              )}
            </div>
          ) : (selectedRsa || selectedFornitore || selectedTipologia) && graphData.edges.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <span className="text-4xl mb-2">🔗</span>
              <p className="text-lg font-medium">Nessun collegamento esistente</p>
              <p className="text-sm mt-1">
                {selectedRsa && 'Questa RSA non ha fornitori associati'}
                {selectedFornitore && 'Questo fornitore non è associato a nessuna RSA'}
                {selectedTipologia && !selectedRsa && !selectedFornitore && 'Nessun fornitore offre questa tipologia di intervento'}
              </p>
              <button
                onClick={handleReset}
                className="mt-4 text-sm text-primary-600 hover:underline"
              >
                Rimuovi filtri
              </button>
            </div>
          ) : (
            <NetworkGraph
              initialNodes={graphData.nodes}
              initialEdges={graphData.edges}
              onNodeDoubleClick={handleNodeDoubleClick}
            />
          )}
        </div>
      </Card>

      {/* Legenda */}
      <GraphLegend stats={graphData.stats} />
    </div>
  );
}

export default MappePage;
